import React, { useState } from 'react';

interface Pair {
  left: string;
  right: string;
}

interface MatchPairsExerciseProps {
  pairs: Pair[];
  onSubmit: (answer: { [key: string]: string }) => void;
}

/**
 * MatchPairsExercise Component
 *
 * Allows users to match items from the left column with items from the right column.
 */
const MatchPairsExercise: React.FC<MatchPairsExerciseProps> = ({ pairs, onSubmit }) => {
  const [matches, setMatches] = useState<{ [key: string]: string }>({});
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);

  const handleLeftClick = (leftItem: string) => {
    setSelectedLeft(leftItem);
  };

  const handleRightClick = (rightItem: string) => {
    if (selectedLeft) {
      setMatches({
        ...matches,
        [selectedLeft]: rightItem
      });
      setSelectedLeft(null);
    }
  };

  const handleClearMatch = (leftItem: string) => {
    const newMatches = { ...matches };
    delete newMatches[leftItem];
    setMatches(newMatches);
  };

  const handleSubmit = () => {
    if (Object.keys(matches).length === pairs.length) {
      onSubmit(matches);
    }
  };

  const isComplete = Object.keys(matches).length === pairs.length;

  return (
    <div className="space-y-6">
      <div className="alert alert-info">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>Click an item on the left, then click its match on the right</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base-content/70 mb-4">Items</h3>
          {pairs.map((pair, index) => {
            const isMatched = matches[pair.left];
            const isSelected = selectedLeft === pair.left;

            return (
              <div key={index} className="relative">
                <button
                  onClick={() => handleLeftClick(pair.left)}
                  className={`
                    btn w-full justify-start text-left h-auto min-h-[3rem] p-4
                    ${isSelected ? 'btn-primary' : isMatched ? 'btn-success' : 'btn-outline'}
                  `}
                  disabled={!!isMatched}
                >
                  <span className="flex-1">{pair.left}</span>
                  {isMatched && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearMatch(pair.left);
                      }}
                      className="btn btn-ghost btn-xs"
                    >
                      ✕
                    </button>
                  )}
                </button>
                {isMatched && (
                  <div className="absolute right-0 top-1/2 transform translate-x-full -translate-y-1/2 px-2">
                    <div className="text-2xl">→</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          <h3 className="font-semibold text-base-content/70 mb-4">Matches</h3>
          {pairs.map((pair, index) => {
            const isMatched = Object.values(matches).includes(pair.right);
            const canSelect = selectedLeft !== null;

            return (
              <button
                key={index}
                onClick={() => handleRightClick(pair.right)}
                className={`
                  btn w-full justify-start text-left h-auto min-h-[3rem] p-4
                  ${isMatched ? 'btn-success' : canSelect ? 'btn-outline hover:btn-primary' : 'btn-outline'}
                `}
                disabled={isMatched || !canSelect}
              >
                {pair.right}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <button
          onClick={handleSubmit}
          className="btn btn-primary btn-lg"
          disabled={!isComplete}
        >
          {isComplete ? 'Submit Answer' : `Match all pairs (${Object.keys(matches).length}/${pairs.length})`}
        </button>
      </div>
    </div>
  );
};

export default MatchPairsExercise;
